<template>
  <div class="lang-switcher" ref="switcherRef">
    <button class="lang-btn" @click="toggleDropdown">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
      </svg>
      <span>{{ currentLabel }}</span>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="arrow" :class="{ open: dropdownOpen }">
        <path d="M6 9l6 6 6-6" />
      </svg>
    </button>
    <div v-if="dropdownOpen" class="lang-dropdown">
      <button
        v-for="lang in languages"
        :key="lang.code"
        class="lang-option"
        :class="{ active: locale === lang.code }"
        @click="switchLang(lang.code)"
      >
        {{ lang.label }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { locale } = useI18n()
const dropdownOpen = ref(false)
const switcherRef = ref<HTMLElement | null>(null)

const languages = [
  { code: 'zh-CN', label: '中文' },
  { code: 'en-US', label: 'English' },
  { code: 'fr-FR', label: 'Français' },
  { code: 'es-ES', label: 'Español' },
  { code: 'it-IT', label: 'Italiano' },
  { code: 'pt-PT', label: 'Português' },
  { code: 'nl-NL', label: 'Nederlands' },
  { code: 'ru-RU', label: 'Русский' },
  { code: 'pl-PL', label: 'Polski' },
  { code: 'sv-SE', label: 'Svenska' },
]

const currentLabel = computed(() => {
  const lang = languages.find(l => l.code === locale.value)
  return lang ? lang.label : '中文'
})

function toggleDropdown() {
  dropdownOpen.value = !dropdownOpen.value
}

function switchLang(code: string) {
  locale.value = code
  dropdownOpen.value = false
  document.documentElement.lang = code.split('-')[0]
}

function onClickOutside(e: MouseEvent) {
  if (switcherRef.value && !switcherRef.value.contains(e.target as Node)) {
    dropdownOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', onClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', onClickOutside)
})
</script>

<style scoped>
.lang-switcher {
  position: relative;
}

.lang-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(0, 212, 255, 0.06);
  border: 1px solid var(--iso-border);
  border-radius: 8px;
  color: var(--iso-text);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.lang-btn:hover {
  border-color: rgba(0, 212, 255, 0.3);
  background: rgba(0, 212, 255, 0.1);
}

.arrow {
  transition: transform 0.2s;
}

.arrow.open {
  transform: rotate(180deg);
}

.lang-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 160px;
  background: var(--iso-surface);
  border: 1px solid var(--iso-border);
  border-radius: 10px;
  padding: 6px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px rgba(0, 212, 255, 0.05);
  z-index: 200;
  animation: iso-fade-in 0.2s ease-out;
}

.lang-option {
  display: block;
  width: 100%;
  padding: 8px 12px;
  background: none;
  border: none;
  border-radius: 6px;
  color: var(--iso-text-dim);
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s;
}

.lang-option:hover {
  background: rgba(0, 212, 255, 0.08);
  color: var(--iso-text);
}

.lang-option.active {
  color: var(--iso-cyan);
  background: rgba(0, 212, 255, 0.1);
}
</style>
